import { useFlyOutProductsBasedOnSelectedCloud } from '../../utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import {
  type CloudAccountChangeParams,
  CloudAccountChangeV2,
  useCurrentCloudProvider,
  providerToAuthCloud,
  useCloudAccounts,
} from '@labrav/common'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  CustomLoading,
  ModalConfirmV2,
  ModalSizeVariants,
  useAuth,
} from '@labrav/react-components'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { CustomProductSelectorDropdown } from '../CustomProductSelectorDropdown/CustomProductSelectorDropdown'
import {
  FlyoutFunction,
  ProductStatus,
} from '../../../modules/productsListing/types'
import { faListCheck } from '@fortawesome/pro-regular-svg-icons'
import { useParams } from 'react-router-dom'
import { isEmpty } from 'lodash'
import { Box } from '@mui/material'
import { useStyles } from './ModalToCreatePrivateOffer.styles'
import { getProducts } from '../../../modules/productsListing/action'
import { useDispatch } from '../../../store'
import { useFlagValue } from '@labrav/flags'

export type PrivateOffersWrapperProps = {
  open?: boolean
  hideCancelIcon?: boolean
  onClose?: () => void
  handleCreatePrivateOffer?: () => void
  handleProductChange?: (productId: string) => void
  selectedAccountIdWithCloud?: string
  handleCloudAccountChange?: (params: CloudAccountChangeParams) => void
  hubspotCloudSelector?: React.ReactNode
  externalBaseUrl?: string
  isCloudDisabled?: boolean
  accountId?: string
}

export const ModalToCreatePrivateOffer: React.FC<PrivateOffersWrapperProps> = ({
  open = false,
  hideCancelIcon = false,
  onClose,
  handleCreatePrivateOffer,
  handleProductChange,
  selectedAccountIdWithCloud,
  handleCloudAccountChange,
  externalBaseUrl,
  isCloudDisabled = false,
  accountId,
}) => {
  const [selectedProductId, setSelectedProductId] = useState<string>('')
  const [productsLoaded, setProductsLoaded] = useState<boolean>(false)
  const { partnerId } = useParams<{ partnerId: string }>()
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const { canDo, loading: authLoading } = useAuth()
  const { value: isAzureMarketplaceMappingManagerEnabled } = useFlagValue(
    'azureMarketplaceMappingManager'
  )
  const cloudMarketplace = useCurrentCloudProvider()
  const { products } = useFlyOutProductsBasedOnSelectedCloud()
  const baseMarketplaceCloudAccounts = useCloudAccounts('flyout')
  const marketplaceCloudAccounts = useMemo(() => {
    if (authLoading) return []
    // Create-offer flow: require marketplace *create* on each account (stricter than list *view*).
    // Azure stays gated by azureMarketplaceMappingManager. GCP is always selectable.
    return baseMarketplaceCloudAccounts.map(account => {
      const hasPermission = canDo({
        cloud: providerToAuthCloud(account.provider),
        service: 'marketplace',
        action: 'create',
      })
      const accountProvider = account.provider.toUpperCase()
      const isAzure = accountProvider === 'AZURE'
      const isGcp = accountProvider === 'GCP'
      return {
        ...account,
        disabled: isAzure
          ? !isAzureMarketplaceMappingManagerEnabled
          : isGcp
            ? false
            : !hasPermission,
      }
    })
  }, [
    authLoading,
    baseMarketplaceCloudAccounts,
    canDo,
    isAzureMarketplaceMappingManagerEnabled,
  ])
  const selectedProductStatus = products.find(
    product => product.productId === selectedProductId
  )?.status

  const handleProductChangeLocal = useCallback(
    (productId: string) => {
      setSelectedProductId(productId)
      handleProductChange?.(productId)
    },
    [handleProductChange]
  )

  useEffect(() => {
    if (!isEmpty(products)) {
      setProductsLoaded(true)
      return
    }

    if (isEmpty(partnerId) || !cloudMarketplace || productsLoaded) {
      return
    }

    const currentPartnerId = accountId || (partnerId as string)
    const fetchProducts = async () => {
      await dispatch(getProducts(currentPartnerId, cloudMarketplace))
      setProductsLoaded(true)
    }
    if (externalBaseUrl && open) {
      fetchProducts()
    } else if (!externalBaseUrl) {
      fetchProducts()
    }
  }, [
    dispatch,
    partnerId,
    cloudMarketplace,
    products,
    productsLoaded,
    open,
    externalBaseUrl,
    accountId,
  ])

  return (
    <ModalConfirmV2
      title="Select a product"
      icon={
        <FontAwesomeIcon
          icon={faListCheck}
          style={{ height: '16px', width: '16px' }}
        />
      }
      description={
        'Select a product for which you’d like to create a private offer.'
      }
      content={
        <>
          {productsLoaded ? (
            <div className={classes.cloudSelectorWrapper}>
              {handleCloudAccountChange && (
                <div className={classes.cloudSelectorWidthHeight}>
                  <CloudAccountChangeV2
                    cloudAccounts={marketplaceCloudAccounts}
                    selectedAccountId={selectedAccountIdWithCloud as string}
                    onChange={handleCloudAccountChange}
                    width={220}
                    size="small"
                    disabled={isCloudDisabled}
                  />
                </div>
              )}
              <CustomProductSelectorDropdown
                functionType={FlyoutFunction.PRIVATE_OFFERS}
                onProductChange={handleProductChangeLocal}
                openNewPrivateOfferModal={open}
              />
            </div>
          ) : (
            <Box className={classes.box}>
              <CustomLoading
                loadingTitle="Products are being fetched"
                loadingSubTitle="It may take a few moments, please wait..."
              />
            </Box>
          )}
        </>
      }
      open={open}
      onAccept={handleCreatePrivateOffer ? handleCreatePrivateOffer : () => {}}
      onDecline={onClose ? onClose : () => {}}
      titleAcceptButton="Create private offer"
      variant={ModalSizeVariants.medium}
      acceptDisabled={
        isEmpty(selectedProductId) ||
        selectedProductStatus === ProductStatus.PRODUCT_STATUS_RESTRICTED
      }
      disabledAcceptButtonMessage={
        selectedProductStatus === ProductStatus.PRODUCT_STATUS_RESTRICTED
          ? 'Offer creation is not allowed by AWS for this restricted product.'
          : 'Select one product to create a private offer.'
      }
      hideCancelIcon={hideCancelIcon}
      hideCancelButton={true}
    />
  )
}

export default ModalToCreatePrivateOffer
